import React, { useState, useEffect, Fragment } from "react";
import { useSearchParams } from "react-router-dom";

import { Message, Form } from "react-bulma-components";
import capacityInformation from "./capacity";

const { Field, Label, Control, Radio, Checkbox } = Form;

export const FORM_STATE = {
  CLOSED: 0,
  SUBMIT_NEW: 1,
  UPDATE: 2,
  CANCEL: 3,
};

export const FORM_HISTORY = {
  IS_NEW: 0,
  WAS_UNKNOWN: 1,
  WAS_UNPAID: 2,
  WAS_SUBMITTED: 3,
  WAS_CANCELLED: 4,
  WAS_DECLINED: 5,
  WAS_WAITLISTED: 6,
};

function ucFirst(text) {
  return text.replace(/^./, (letter) => letter.toUpperCase());
}

export function itemForm(item) {
  const { item_type, item_code, activity_short_title } = item;
  if (item_type === "attendance" || item_type === "purchase")
    return {
      formName: "registration",
      formTitle: "Registration Form",
      formType: "registration",
    };
  else {
    const suffix = activity_short_title ? ` for ${activity_short_title}` : "";
    return {
      formName: `${item_code} ${item_type}` + suffix,
      formTitle: `${ucFirst(item_code)} ${ucFirst(item_type)} Form` + suffix,
      formType: item_type,
    };
  }
}

function itemHistory(item, participation) {
  const { item_type, item_code, activity_id } = item;
  const {
    IS_NEW,
    WAS_UNKNOWN,
    WAS_UNPAID,
    WAS_SUBMITTED,
    WAS_CANCELLED,
    WAS_DECLINED,
    // WAS_WAITLISTED is checked for by the caller, so it can determine
    // waitlist position, so need not be checked for here.
  } = FORM_HISTORY;
  const previousData = participation.current_registration_item_data;
  const previousParticipationData = previousData?.[activity_id];
  if (!previousParticipationData) return IS_NEW;
  const previousFormData = previousParticipationData[item_type]?.[item_code];
  const owing = previousFormData?.current_state?.amount_owing ?? 0;
  if (item_type === "attendance") {
    const attending = previousFormData?.item_values?.attending ?? null;
    const onceAttended = !!previousFormData?.current_state?.confirmed_at;
    return attending === null
      ? WAS_UNKNOWN
      : owing > 0
      ? WAS_UNPAID
      : attending
      ? WAS_SUBMITTED
      : onceAttended
      ? WAS_CANCELLED
      : WAS_DECLINED;
  } else if (item_type === "purchase")
    // It is technically possible for a purchase to be attached to a
    // participation record without any attendance information, so just
    // return WAS_UNKNOWN here and let the records from attendance
    // items push that to WAS_SUBMITTED or WAS_CANCELLED or WAS_DECLINED
    // if appropriate.
    return owing > 0 ? WAS_UNPAID : WAS_UNKNOWN;
  else return previousFormData ? WAS_SUBMITTED : IS_NEW;
}

export function useFormState(participation) {
  const { portal_registration_items } = participation;
  const historyState = {};
  const waitlistStatus = {};
  const {
    IS_NEW,
    WAS_UNKNOWN,
    WAS_UNPAID,
    WAS_SUBMITTED,
    WAS_CANCELLED,
    WAS_DECLINED,
    WAS_WAITLISTED,
  } = FORM_HISTORY;
  const { CLOSED, SUBMIT_NEW, UPDATE, CANCEL } = FORM_STATE;

  portal_registration_items.forEach((item) => {
    const form = itemForm(item).formName;
    const { waitingList, available } = capacityInformation(item);
    const rawHistory = itemHistory(item, participation);
    const history =
      waitingList.position !== null &&
      !waitingList.positionIsForInPerson &&
      rawHistory !== WAS_SUBMITTED &&
      rawHistory !== WAS_UNPAID
        ? WAS_WAITLISTED
        : rawHistory;

    if (historyState[form] === undefined) historyState[form] = history;
    if (waitlistStatus[form] === undefined && history === WAS_WAITLISTED) {
      waitlistStatus[form] = {
        position: 1 + waitingList.position,
        nowAvailable: available.toMe > 0,
      };
    }
    if (history === WAS_SUBMITTED) historyState[form] = history;
    if (history === WAS_WAITLISTED && historyState[form] !== WAS_SUBMITTED)
      historyState[form] = history;

    if (
      (history === WAS_CANCELLED || history === WAS_DECLINED) &&
      (historyState[form] === IS_NEW ||
        historyState[form] === WAS_UNKNOWN ||
        historyState[form] === WAS_UNPAID)
    )
      historyState[form] = history;
    if (
      history === WAS_UNPAID &&
      (historyState[form] === IS_NEW || historyState[form] === WAS_UNKNOWN)
    )
      historyState[form] = history;

    if (history === WAS_UNKNOWN && historyState[form] === IS_NEW)
      historyState[form] = history;
  });

  const itemFormList = portal_registration_items.map(itemForm);

  const formNameList = [...new Set(itemFormList.map((i) => i.formName))];
  const formTypeList = [...new Set(itemFormList.map((i) => i.formType))];
  const formTypeLookup = Object.fromEntries(
    itemFormList.map((i) => [i.formName, i.formType])
  );
  const formTitleLookup = Object.fromEntries(
    itemFormList.map((i) => [i.formName, i.formTitle])
  );

  const singleForm = formNameList.length === 1 ? formNameList[0] : null;
  const alwaysOpen = singleForm && historyState[singleForm] === IS_NEW;

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFormState = {
    registration:
      searchParams.get("register") === null
        ? CLOSED
        : historyState.registration === WAS_SUBMITTED
        ? UPDATE
        : SUBMIT_NEW,
  };

  useEffect(() => {
    if (searchParams.size > 0) setSearchParams({}, { replace: true });
  });

  const [openState, setOpenState] = useState(
    alwaysOpen
      ? { [singleForm]: SUBMIT_NEW }
      : Object.fromEntries(
          formNameList.map((form) => [form, defaultFormState[form] ?? CLOSED])
        )
  );

  const singleFormClosed = openState[singleForm] === FORM_STATE.CLOSED;

  useEffect(() => {
    if (alwaysOpen && singleFormClosed)
      setOpenState({ [singleForm]: FORM_STATE.SUBMIT_NEW });
  }, [alwaysOpen, singleForm, singleFormClosed]);

  const formState = Object.fromEntries(
    formNameList.map((form) => [
      form,

      {
        openFor: openState[form] ?? CLOSED,
        history: historyState[form] ?? IS_NEW,
        isOnlyForm: !!singleForm,
        alwaysOpen,
        formTitle: formTitleLookup[form],
        waitlistStatus: waitlistStatus[form],
      },
    ])
  );

  const setFormOpenFor = (form, openFor) =>
    setOpenState((s) => {
      return { ...s, [form]: openFor };
    });

  const formActions = [];

  formTypeList.forEach((formType) => {
    const forms = formNameList.filter(
      (form) => formTypeLookup[form] === formType
    );
    const haveNew = forms.map((form) => openState[form]).includes(SUBMIT_NEW);
    const haveUpdate = forms.map((form) => openState[form]).includes(UPDATE);
    const haveCancel = forms.map((form) => openState[form]).includes(CANCEL);
    const declining =
      formType === "registration" &&
      !forms.map((form) => historyState[form]).includes(WAS_SUBMITTED) &&
      !forms.map((form) => historyState[form]).includes(WAS_UNPAID);

    const cancelAction = declining ? "Submit" : "Cancel";
    const typeNoun = ucFirst(forms.length > 1 ? formType + "s" : formType);

    // Any one action by itself: use that as the action name. Multiple
    // actions (such as submitting a new application form of one type
    // while updating or cancelling another) we just call an "Update".
    const [action, noun] =
      haveNew && !haveUpdate && !haveCancel
        ? ["Submit", typeNoun]
        : haveCancel && !haveNew && !haveUpdate
        ? [cancelAction, declining ? '"Not Attending"' : typeNoun]
        : haveNew || haveUpdate || haveCancel
        ? ["Update", typeNoun]
        : [];

    if (action)
      formActions.push({
        action,
        noun,
      });
  });

  const distinctActions = [...new Set(formActions.map((a) => a.action))];

  const submitTextElements = formActions.map(({ action, noun }, position) =>
    position > 0 && distinctActions.length === 1 ? noun : `${action} ${noun}`
  );

  const submitText = [
    submitTextElements.slice(0, -1).join(", "),
    submitTextElements.slice(-1).flat(),
  ]
    .filter((t) => t !== "")
    .join(" and ");

  return { formNameList, formState, setFormOpenFor, alwaysOpen, submitText };
}

function ShowPreviouslySubmittedForm({ form, openFor, setOpenFor, unPaid }) {
  const { CLOSED, UPDATE, CANCEL } = FORM_STATE;
  return (
    <Message color={unPaid ? "warning" : "success"}>
      <Message.Header>
        {ucFirst(form)} Already Submitted
        {unPaid ? ", but Full Payment Not Yet Made" : null}
      </Message.Header>
      <Message.Body>
        <Field>
          <Control>
            <Checkbox
              checked={openFor !== CLOSED}
              onChange={(e) => {
                if (e.target.checked && openFor === CLOSED) setOpenFor(UPDATE);
                if (!e.target.checked) setOpenFor(CLOSED);
              }}
            >
              {form === "registration"
                ? unPaid
                  ? "Open registration/payment form, to make payment " +
                    "(or other changes)"
                  : "View, change or cancel registration"
                : "Re-open form to make changes"}
            </Checkbox>
          </Control>
          {form === "registration" && openFor !== CLOSED ? (
            <Fragment>
              <Control>
                <Radio
                  name="registration-change-or-cancel"
                  checked={openFor === UPDATE}
                  value={UPDATE}
                  onChange={(e) => setOpenFor(UPDATE)}
                >
                  {unPaid ? "Make payment (or make changes)" : "Make changes"}
                </Radio>
              </Control>
              <Control>
                <Radio
                  name="registration-change-or-cancel"
                  checked={openFor === CANCEL}
                  value={CANCEL}
                  onChange={(e) => setOpenFor(CANCEL)}
                >
                  Cancel entire registration
                </Radio>
              </Control>
            </Fragment>
          ) : null}
        </Field>
      </Message.Body>
    </Message>
  );
}

function ShowPreviouslyCancelledForm({ form, openFor, setOpenFor }) {
  const { CLOSED, SUBMIT_NEW } = FORM_STATE;
  return (
    <Message color="danger">
      <Message.Header>{ucFirst(form)} Previously Cancelled</Message.Header>
      <Message.Body>
        <Field>
          <Control>
            <Checkbox
              checked={openFor !== CLOSED}
              onChange={(e) => {
                if (e.target.checked && openFor === CLOSED)
                  setOpenFor(SUBMIT_NEW);
                if (!e.target.checked) setOpenFor(CLOSED);
              }}
            >
              Submit a new {form}
            </Checkbox>
          </Control>
        </Field>
      </Message.Body>
    </Message>
  );
}

function ShowPreviouslyDeclinedForm({ form, openFor, setOpenFor }) {
  const { CLOSED, SUBMIT_NEW } = FORM_STATE;
  return (
    <Message color="warning">
      <Message.Header>
        Previous {ucFirst(form)} Indicated "Not Attending"
      </Message.Header>
      <Message.Body>
        <Field>
          <Control>
            <Checkbox
              checked={openFor !== CLOSED}
              onChange={(e) => {
                if (e.target.checked && openFor === CLOSED)
                  setOpenFor(SUBMIT_NEW);
                if (!e.target.checked) setOpenFor(CLOSED);
              }}
            >
              Re-open {form}
            </Checkbox>
          </Control>
        </Field>
      </Message.Body>
    </Message>
  );
}

function ShowPreviouslyWaitlistedForm({ form, openFor, setOpenFor }) {
  const { CLOSED, SUBMIT_NEW, CANCEL } = FORM_STATE;
  return (
    <Message color="success">
      <Message.Header>Space Is Now Available!</Message.Header>
      <Message.Body>
        <Field>
          <Control>
            <Checkbox
              checked={openFor === SUBMIT_NEW}
              onChange={(e) => {
                if (e.target.checked && openFor !== SUBMIT_NEW)
                  setOpenFor(SUBMIT_NEW);
                if (!e.target.checked && openFor === SUBMIT_NEW)
                  setOpenFor(CLOSED);
              }}
            >
              Launch {form}
            </Checkbox>
          </Control>
          <Control>
            <Checkbox
              checked={openFor === CANCEL}
              value={CANCEL}
              onChange={(e) => setOpenFor(CANCEL)}
            >
              Cancel waiting list request
            </Checkbox>
          </Control>
        </Field>
      </Message.Body>
    </Message>
  );
}

function ShowCurrentlyWaitlistedForm({ form, openFor, setOpenFor, position }) {
  const { CLOSED, CANCEL } = FORM_STATE;
  return (
    <Message color="danger">
      <Message.Header>No space available - On Waiting List</Message.Header>
      <Message.Body>
        You are currently at position {position} on the waiting list.
        <Field>
          <Control>
            <Checkbox
              checked={openFor === CANCEL}
              onChange={(e) => {
                if (e.target.checked) setOpenFor(CANCEL);
                if (!e.target.checked) setOpenFor(CLOSED);
              }}
            >
              I no longer want to be on the waiting list
            </Checkbox>
          </Control>
        </Field>
      </Message.Body>
    </Message>
  );
}

function ShowWaitlistedForm({ form, openFor, setOpenFor, waitlistStatus }) {
  const position = waitlistStatus.position;
  return waitlistStatus.nowAvailable ? (
    <ShowPreviouslyWaitlistedForm {...{ form, openFor, setOpenFor }} />
  ) : (
    <ShowCurrentlyWaitlistedForm {...{ form, openFor, setOpenFor, position }} />
  );
}

function ShowPreviouslyUnknownForm({ form, openFor, setOpenFor }) {
  const { SUBMIT_NEW, CANCEL } = FORM_STATE;
  return (
    <Message color="warning">
      <Message.Header>{ucFirst(form)} Currently Unconfirmed</Message.Header>
      <Message.Body>
        <Field>
          {form === "registration" ? <Label>Are you attending?</Label> : null}
          <Control>
            <Radio
              name="registration-confirm"
              checked={openFor === SUBMIT_NEW}
              value={SUBMIT_NEW}
              onChange={(e) => setOpenFor(SUBMIT_NEW)}
            >
              {form === "registration"
                ? " Yes; open registration form"
                : `Submit ${form}`}
            </Radio>
          </Control>
          <Control>
            <Radio
              name="registration-confirm"
              checked={openFor === CANCEL}
              value={CANCEL}
              onChange={(e) => setOpenFor(CANCEL)}
            >
              {form === "registration"
                ? ' No; please mark me as "Not Attending"'
                : `Do not submit ${form}`}
            </Radio>
          </Control>
        </Field>
      </Message.Body>
    </Message>
  );
}

function ShowForm({ form, state, setOpenFor }) {
  const { CLOSED, SUBMIT_NEW } = FORM_STATE;
  const { openFor, history, alwaysOpen, formTitle, waitlistStatus } = state;
  if (alwaysOpen) return null;
  switch (history) {
    case FORM_HISTORY.WAS_SUBMITTED:
      return <ShowPreviouslySubmittedForm {...{ form, openFor, setOpenFor }} />;
    case FORM_HISTORY.WAS_CANCELLED:
      return <ShowPreviouslyCancelledForm {...{ form, openFor, setOpenFor }} />;
    case FORM_HISTORY.WAS_DECLINED:
      return <ShowPreviouslyDeclinedForm {...{ form, openFor, setOpenFor }} />;
    case FORM_HISTORY.WAS_WAITLISTED:
      return (
        <ShowWaitlistedForm
          {...{ form, openFor, setOpenFor, waitlistStatus }}
        />
      );
    case FORM_HISTORY.WAS_UNKNOWN:
      return <ShowPreviouslyUnknownForm {...{ form, openFor, setOpenFor }} />;
    case FORM_HISTORY.WAS_UNPAID:
      return (
        <ShowPreviouslySubmittedForm
          {...{ form, openFor, setOpenFor }}
          unPaid
        />
      );
    default:
      return (
        <Control>
          <Checkbox
            checked={openFor !== CLOSED}
            onChange={(e) => setOpenFor(e.target.checked ? SUBMIT_NEW : CLOSED)}
          >
            {" "}
            {formTitle}
          </Checkbox>
        </Control>
      );
  }
}

export function ShowForms({
  formNameList,
  formState,
  alwaysOpen,
  setFormOpenFor,
}) {
  const newForms = formNameList.filter(
    (form) => formState[form].history === FORM_HISTORY.IS_NEW
  );
  const oldForms = formNameList.filter(
    (form) => formState[form].history !== FORM_HISTORY.IS_NEW
  );

  const newFormHeader =
    oldForms.length === 0
      ? "Please select desired form(s):"
      : "Also available; please select if desired:";
  return (
    <Fragment>
      {oldForms.map((form) => {
        const state = formState[form];
        const setOpenFor = (openFor) => setFormOpenFor(form, openFor);

        return <ShowForm key={form} {...{ form, state, setOpenFor }} />;
      })}
      {newForms.length > 0 && !alwaysOpen ? (
        <Field>
          <Label>{newFormHeader}</Label>
          {newForms.map((form) => {
            const state = formState[form];
            const setOpenFor = (openFor) => setFormOpenFor(form, openFor);

            return <ShowForm key={form} {...{ form, state, setOpenFor }} />;
          })}
        </Field>
      ) : null}
    </Fragment>
  );
}
